import { Component, OnDestroy, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { Globals } from "../../common/global";
import { ConfigApi } from "../../config-api";
import { ApiCallingService } from "../../services/api-calling.service";

@Component({
  selector: "app-lottery",
  templateUrl: "./lottery.component.html",
  styleUrls: ["./lottery.component.css"],
})
export class LotteryComponent implements OnInit, OnDestroy {
  list: any[] = [];
  page: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  loading: boolean = false;
  userDetails: any;
  modeltitle: string = "";
  detail: any = {};
  lotteryToDelete: string = "";
  searchText: string = "";
  winnerSelectionTypes: string[] = ["multiple", "single"];
  private searchTimer: any;

  constructor(
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    public globals: Globals,
    private modalService: NgbModal,
  ) {
    const data = localStorage.getItem("Auth-user");
    if (!data) return;
    try {
      this.userDetails = JSON.parse(data);
    } catch (error) {
      console.error(error);
    }
  }

  ngOnInit(): void {
    this.getList();
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
  }

  onSearchChange() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page = 1;
      this.getList();
    }, 400);
  }

  getList() {
    this.loading = true;
    const url =
      ConfigApi.URLS.LOTTERY.LIST +
      `?operator_id=${this.userDetails._id}&page=${this.page - 1}&limit=${this.pageSize}&search=${this.searchText}`;

    this.apiCallingService.callAPI({}, "GET", url).subscribe(
      (res) => {
        if (this.apiCallingService.handleSuccess(res)) {
          this.list = res.data || [];
          this.totalCount = res.total || 0;
        }
        this.loading = false;
      },
      (error) => {
        console.error(error);
        this.toastr.error("Failed to load lotteries");
        this.loading = false;
      },
    );
  }

  openModal(targetModal: any, data: any, title: string) {
    this.modeltitle = title;
    if (data && data._id) {
      this.detail = {
        ...data,
        expiryDate: data.expiryDate
          ? new Date(data.expiryDate).toISOString().split("T")[0]
          : "",
        winnerConfiguration: data.winnerConfiguration
          ? [...data.winnerConfiguration.map((w: any) => ({ ...w }))]
          : [{ rank: 1, prizeAmount: 0 }],
      };
    } else {
      this.detail = {
        lotteryName: "",
        expiryDate: "",
        requiredOrders: null,
        winnerSelectionType: "multiple",
        winnerConfiguration: [{ rank: 1, prizeAmount: 0 }],
        minimumOrderValue: 0,
      };
    }
    this.modalService.open(targetModal, {
      centered: true,
      backdrop: "static",
      size: "lg",
    });
  }

  onWinnerTypeChange() {
    if (this.detail.winnerSelectionType === "single") {
      this.detail.winnerConfiguration = [this.detail.winnerConfiguration[0]];
    }
  }

  addWinnerRow() {
    const nextRank = this.detail.winnerConfiguration.length + 1;
    this.detail.winnerConfiguration.push({ rank: nextRank, prizeAmount: 0 });
  }

  removeWinnerRow(index: number) {
    if (this.detail.winnerConfiguration.length > 1) {
      this.detail.winnerConfiguration.splice(index, 1);
    }
  }

  onSubmit() {
    if (this.detail._id) {
      this.updateLottery();
    } else {
      this.createLottery();
    }
  }

  createLottery() {
    const payload = {
      operator_id: this.userDetails._id,
      lotteryName: this.detail.lotteryName,
      expiryDate: this.detail.expiryDate,
      requiredOrders: this.detail.requiredOrders,
      winnerSelectionType: this.detail.winnerSelectionType,
      winnerConfiguration: this.detail.winnerConfiguration,
      minimumOrderValue: this.detail.minimumOrderValue,
    };

    this.apiCallingService
      .callAPI(payload, "POST", ConfigApi.URLS.LOTTERY.CREATE)
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(res.msg || "Lottery created successfully");
            this.closeBtnClick();
            this.getList();
          }
        },
        (error) => {
          console.error(error);
          this.toastr.error(error.error.msg || "Failed to create lottery");
        },
      );
  }

  updateLottery() {
    const payload = {
      lotteryName: this.detail.lotteryName,
      expiryDate: this.detail.expiryDate,
      requiredOrders: this.detail.requiredOrders,
      winnerConfiguration: this.detail.winnerConfiguration,
      minimumOrderValue: this.detail.minimumOrderValue,
    };

    this.apiCallingService
      .callAPI(
        payload,
        "PUT",
        ConfigApi.URLS.LOTTERY.UPDATE + `/${this.detail._id}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(res.msg || "Lottery updated successfully");
            this.closeBtnClick();
            this.getList();
          }
        },
        (error) => {
          console.error(error);
          this.toastr.error("Failed to update lottery");
        },
      );
  }

  toggleStatus(item: any) {
    const payload = { isEnabled: item.isEnabled };

    this.apiCallingService
      .callAPI(
        payload,
        "PUT",
        ConfigApi.URLS.LOTTERY.TOGGLE_STATUS + `/${item._id}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            const status = item.isEnabled ? "enabled" : "disabled";
            this.toastr.success(`Lottery ${status} successfully`);
          }
        },
        (error) => {
          console.error(error);
          item.isEnabled = !item.isEnabled;
          this.toastr.error("Failed to update lottery status");
        },
      );
  }

  openDeleteModal(deleteModal: any, id: string) {
    this.lotteryToDelete = id;
    this.modalService.open(deleteModal, {
      centered: true,
      backdrop: "static",
      size: "sm",
    });
  }

  confirmDelete() {
    if (!this.lotteryToDelete) return;

    this.apiCallingService
      .callAPI(
        {},
        "DELETE",
        ConfigApi.URLS.LOTTERY.DELETE + `/${this.lotteryToDelete}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(res.msg || "Lottery deleted successfully");
            this.closeDeleteModalClick();
            this.getList();
          }
        },
        (error) => {
          console.error(error);
          this.toastr.error("Failed to delete lottery");
        },
      );
  }

  loadPage(page: number) {
    this.page = page;
    this.getList();
  }

  closeBtnClick() {
    this.modalService.dismissAll();
    this.detail = {};
  }

  closeDeleteModalClick() {
    this.modalService.dismissAll();
    this.lotteryToDelete = "";
  }

  trackById(_index: number, item: any) {
    return item._id;
  }
}
